import React, { useState, useEffect } from 'react'
import { FileText, Download, Calendar, DollarSign, Eye } from 'lucide-react'
import { paymentAPI } from '../services/api'
import type { Payment } from '../types'
import InvoiceGenerator from '../components/InvoiceGenerator'

interface Invoice {
  id: string
  invoice_number: string
  amount: number
  currency: string
  status: 'paid' | 'pending' | 'overdue'
  issue_date: string
  due_date: string
  payment_date?: string
  plan_name: string
  pdf_url?: string
  payment?: Payment
}

const UserInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false)

  useEffect(() => {
    fetchUserInvoices()
  }, [])

  const fetchUserInvoices = async () => {
    try {
      setLoading(true)
      const response = await paymentAPI.getPaymentHistory()
      // const user = JSON.parse(localStorage.getItem('user') || '{}')
      
      // Debug: sprawdź strukturę danych
      console.log('User payments response:', response)
      console.log('First payment:', response.data[0])
      
      // Konwertuj płatności na faktury
      const paymentsAsInvoices = response.data
        .filter((payment: Payment) => payment.status === 'completed')
        .map((payment: Payment) => {
          // Określ plan na podstawie kwoty jeśli brak plan_name
          let planName = payment.plan_name
          if (!planName) {
            if (payment.amount === 4.99 || payment.amount === 24.99) {
              planName = 'Standard'
            } else if (payment.amount === 7.99 || payment.amount === 59.99) {
              planName = 'Pro'  
            } else if (payment.amount === 699 || payment.amount === 699.00) {
              planName = 'Unlimited'
            }
          }
          
          return {
            id: payment.id.toString(),
            invoice_number: `INV-${new Date(payment.created_at).getFullYear()}-${String(payment.id).padStart(6, '0')}`,
            amount: payment.amount,
            currency: payment.currency,
            status: 'paid' as const,
            issue_date: payment.created_at,
            due_date: payment.created_at, // Płatność natychmiastowa
            payment_date: payment.created_at,
            plan_name: planName || 'PresentClic Plan',
            payment: { ...payment, plan_name: planName } // Użyj danych z API
          }
        })
      
      setInvoices(paymentsAsInvoices)
    } catch (error) {
      console.error('Error fetching invoices:', error)
      setInvoices([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'overdue':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleDownloadInvoice = (invoice: Invoice) => {
    if (invoice.payment) {
      setSelectedPayment(invoice.payment)
      setShowInvoiceGenerator(true)
    }
  }

  const totalPaid = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0)

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 p-6">
        <div className="flex items-center justify-center">
          <div className="text-white">Loading invoices...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Invoice History</h1>
        <p className="text-gray-400">View and download your invoices</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-green-600/10 rounded-lg">
              <DollarSign className="h-8 w-8 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Total Paid</p>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(totalPaid, invoices[0]?.currency || 'USD')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-blue-600/10 rounded-lg">
              <FileText className="h-8 w-8 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Total Invoices</p>
              <p className="text-2xl font-bold text-white">{invoices.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 p-6 rounded-lg border border-dark-700">
          <div className="flex items-center">
            <div className="p-3 bg-purple-600/10 rounded-lg">
              <Calendar className="h-8 w-8 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Latest Invoice</p>
              <p className="text-lg font-bold text-white">
                {invoices.length > 0 ? formatDate(invoices[0].issue_date) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-dark-800 rounded-lg overflow-hidden border border-dark-700">
        <div className="px-6 py-4 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FileText className="h-5 w-5 text-primary mr-2" />
            Your Invoices
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Invoice</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Plan</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Amount</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Status</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Issue Date</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Due Date</th>
                <th className="text-left py-3 px-6 text-gray-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-400 mb-2">No Invoices Yet</h3>
                    <p className="text-gray-500">Your invoices will appear here after your first payment.</p>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-dark-600 hover:bg-dark-700">
                    <td className="py-4 px-6">
                      <div>
                        <div className="text-white font-medium">{invoice.invoice_number}</div>
                        {invoice.payment_date && (
                          <div className="text-gray-400 text-sm">
                            Paid: {formatDate(invoice.payment_date)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {invoice.plan_name}
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-white font-medium">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {formatDate(invoice.issue_date)}
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {formatDate(invoice.due_date)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDownloadInvoice(invoice)}
                          className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          Download
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(invoice)}
                          className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Billing Information Notice */}
      <div className="bg-blue-600/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start">
          <FileText className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-blue-400 font-medium mb-1">Need to update your billing information?</h3>
            <p className="text-blue-300 text-sm">
              You can update your billing address and company details in your{' '}
              <a href="/profile" className="underline hover:text-blue-200">
                profile settings
              </a>
              . This information will be used for future invoices.
            </p>
          </div>
        </div>
      </div>

      {/* Invoice Generator */}
      {selectedPayment && (
        <InvoiceGenerator
          payment={selectedPayment as any}
          isOpen={showInvoiceGenerator}
          onClose={() => {
            setShowInvoiceGenerator(false)
            setSelectedPayment(null)
          }}
        />
      )}
    </div>
  )
}

export default UserInvoices